import { PublicOfferingOptionsByOfferIdSuccessListResponse } from '@genial/services';
import { ResponsesPublicOfferingResponse } from '@genial/services/src/models';
import { Utils } from '@genial/utils';

import { PublicOfferTypes } from '../external/constants';
import {
  OfertaPublica,
  PublicOfferModality,
  ReturnTypeOf_ObterDetalhesOfertaPublica,
  ReturnTypeOf_ProdutosObterProdutosDisponiveis,
  ReturnTypeOf_SolicitarAtivoParaAplicacao,
} from '../external/types';
import {
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
  PublicOfferDetailsNormalized,
  PublicOfferEntityNormalized,
} from '../types';

export const getPublicOfferType = (offer: OfertaPublica): PublicOfferTypes => {
  const getEquityType = (): PublicOfferTypes => {
    switch (offer.codigoLimitador) {
      case 0:
        return PublicOfferTypes.EQUITY_MAX_VALUE_FOREACH;
      case 2:
        return PublicOfferTypes.EQUITY_FIXED;
      default:
        return PublicOfferTypes.NOT_SET;
    }
  };

  const getDebentureType = (): PublicOfferTypes => {
    switch (offer.codigoLimitador) {
      case 0:
        return PublicOfferTypes.DEB_MAX_VALUE_FOREACH;
      case 1:
        return PublicOfferTypes.DEB_MINIMUM_REMUNERATION;
      case 2:
        return PublicOfferTypes.DEB_FIXED;
      default:
        return PublicOfferTypes.NOT_SET;
    }
  };

  const getFIIType = (): PublicOfferTypes => {
    switch (offer.codigoLimitador) {
      case 1:
        return PublicOfferTypes.FII_MINIMUM_REMUNERATION;
      case 2:
        return PublicOfferTypes.FII_FIXED;
      default:
        return PublicOfferTypes.NOT_SET;
    }
  };

  switch (offer.tipoOferta.id) {
    case 1:
      return getEquityType();
    case 2:
      return getDebentureType();
    case 3:
      return PublicOfferTypes.CRI_MINIMUM_REMUNERATION;
    case 4:
      return PublicOfferTypes.CRA_MINIMUM_REMUNERATION;
    case 5:
      return PublicOfferTypes.COE_FIXED;
    case 6:
      return getFIIType();
    case 7:
      return PublicOfferTypes.FIDC_FIXED;
    default:
      return PublicOfferTypes.NOT_SET;
  }
};

export const normalizePublicOffers = (
  data: ReturnTypeOf_ProdutosObterProdutosDisponiveis
): PublicOfferEntityNormalized[] => {
  if (!data || !data.ativosOfertaPublica) {
    return [];
  }

  try {
    return data.ativosOfertaPublica.map(
      (publicOffer) =>
        ({
          publicOfferType: getPublicOfferType(publicOffer),
          offerId: publicOffer.numeroOferta || 0,
          offerType: publicOffer.tipoOferta?.nome || '',
          offerTypeId: publicOffer.tipoOferta?.id.toString() ?? '0',
          suitabilityProfile: publicOffer.perfilSuitability || '',
          isQualified: publicOffer.isQualificado || false,
          isProfessional: publicOffer.isProfissional || false,
          companyName: publicOffer.nomeEmpresa || '',
          offerName: publicOffer.descricaoOferta || '',
          isinCode: publicOffer.codigoIsin || '',
          alterationDateNormalized: publicOffer.dataAlteracao
            ? new Date(publicOffer.dataAlteracao).getTime()
            : 0,
          alterationDateFormatted: publicOffer.dataAlteracao
            ? Utils.Mask.DateFormat(new Date(publicOffer.dataAlteracao), 'dd/MM/yyyy')
            : '--',
          negotiationDateNormalized: publicOffer.dataNegociacao
            ? new Date(publicOffer.dataNegociacao).getTime()
            : 0,
          negotiationDateFormatted: publicOffer.dataNegociacao
            ? Utils.Mask.DateFormat(new Date(publicOffer.dataNegociacao), 'dd/MM/yyyy')
            : '--',
          liquidityDateNormalized: publicOffer.dataLiquidacao
            ? new Date(publicOffer.dataLiquidacao).getTime()
            : 0,
          liquidityDateFormatted: publicOffer.dataLiquidacao
            ? Utils.Mask.DateFormat(new Date(publicOffer.dataLiquidacao), 'dd/MM/yyyy')
            : '--',
          limiter: publicOffer.limitador || '',
          limiterCode: publicOffer.codigoLimitador || 0,
          deadlineMonths: publicOffer.prazoEmMeses || 0,
          minimumRemuneration: publicOffer.valorTaxaMinima || 0,
          maximumRemuneration: publicOffer.valorTaxaMaxima || 100000000,
          maximumRemunerationAsReceived: publicOffer.valorTaxaMaxima,
          minimumEquityValue: publicOffer.valorTaxaMinima || 0,
          maximumEquityValue: publicOffer.valorTaxaMaxima || 100000000,
          relationshipIndex: publicOffer.relacaoIndice || '',
          benchmarkName: publicOffer.nomeBenchmark || '',
          documents: (publicOffer.listaArquivos || []).map((file) => ({
            fileAvailable: !file.excluido,
            fileName: file.nome || '',
            fileUrl: file.link || '',
          })),
        } as PublicOfferEntityNormalized)
    );
  } catch (_error) {
    return [];
  }
};

const normalizeExtremeValuesAndLabels = (modality: PublicOfferModality, unitaryPrice: number) => {
  const isQuantity = modality.tipoModalidade === 'Quantidade de Ações';

  const minimumApplication = modality.valorMinAdesao;

  const minimumQuantity = isQuantity
    ? modality.valorMinAdesao
    : modality.valorMinAdesao / unitaryPrice;

  const maximumApplication = modality.valorMaxAdesao;

  const maximumQuantity = isQuantity
    ? modality.valorMaxAdesao
    : modality.valorMaxAdesao / unitaryPrice;

  return {
    minimumQuantity,
    maximumQuantity,
    minimumApplication,
    maximumApplication,
    minimumApplicationFormatted: isQuantity
      ? Utils.Mask.Quantity(minimumApplication)
      : Utils.Mask.Money(minimumApplication),
    maximumApplicationFormatted: isQuantity
      ? Utils.Mask.Quantity(maximumApplication)
      : Utils.Mask.Money(maximumApplication),
  };
};

export const normalizePublicOfferDetails = (
  data: ReturnTypeOf_ObterDetalhesOfertaPublica,
  unitaryPrice: number = 1
): PublicOfferDetailsNormalized => {
  if (!data || !data.length) {
    return { modalities: [] };
  }

  try {
    return {
      modalities: data.map(
        (modality) =>
          ({
            ...modality,
            modalityName: modality.nomeModalidade || '',
            modalityDescription: modality.descricaoModalidade || '',
            modalityType: modality.tipoModalidade || '',
            reservationStartDateFormatted: Utils.Mask.DateFormat(
              new Date(modality.dataInicioReserva),
              'dd/MM/yyyy'
            ),
            reservationEndDateFormatted: Utils.Mask.DateFormat(
              new Date(modality.dataFimReserva),
              'dd/MM/yyyy'
            ),
            ...normalizeExtremeValuesAndLabels(modality, unitaryPrice),
            isExpired: Utils.Date.IsPast(new Date(modality.dataFimReserva)),
            linked: !!modality.vinculado,
          } as PublicOfferDetailsModalityNormalized)
      ),
    };
  } catch (_error) {
    return { modalities: [] };
  }
};

const createTimelineItem = (
  date: string | null | undefined,
  label: string,
  hideIfCoe: boolean = false
): PublicOfferDetailsModalityWithTimelineNormalized['timeline'] => {
  if (!date || hideIfCoe) {
    return [];
  }

  const dateFormat =
    Utils.Mask.DateFormat(new Date(date), 'HH:mm') === '00:00' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm';

  return [
    {
      label,
      date: new Date(date),
      formattedDate: Utils.Mask.DateFormat(new Date(date), dateFormat),
    },
  ];
};

const normalizePublicOfferModalitiesWithTimeline = (
  data: ReturnTypeOf_SolicitarAtivoParaAplicacao,
  modalities: PublicOfferDetailsModalityNormalized[]
): PublicOfferDetailsModalityWithTimelineNormalized[] => {
  return modalities.map((modality) => ({
    ...modality,
    timeline: [
      ...createTimelineItem(data.dataNegociacao, 'Início Negociação'),
      ...createTimelineItem(data.dataLiquidacao, 'Liquidação', data.tipoOferta.id === 5),
      ...createTimelineItem(data.dataInicioReserva, 'Início Reserva', data.tipoOferta.id === 5),
      ...createTimelineItem(data.dataBookBuilding, 'Book Building', data.tipoOferta.id === 5),
      ...createTimelineItem(
        data.dataMaxModalidadesNaoVinculadas,
        'Encerramento não Vinculados',
        data.tipoOferta.id === 5
      ),
      ...createTimelineItem(
        data.dataMaxModalidadesVinculadas,
        'Encerramento Vinculados',
        data.tipoOferta.id === 5
      ),
      ...createTimelineItem(modality.dataFimReserva, 'Limite para Reserva'),
    ].sort((a, b) => {
      return a.date.getTime() - b.date.getTime();
    }),
  }));
};

export const normalizePublicOfferAssetToInvestDetails = (
  data: ReturnTypeOf_SolicitarAtivoParaAplicacao
): PublicOfferAssetToInvestNormalized => {
  try {
    const unitaryPrice = data.valorPU || 0;
    const { modalities } = normalizePublicOfferDetails(data.modalidades, unitaryPrice);

    const linkedModalities = modalities.filter((modality) => modality.linked);
    const unlinkedModalities = modalities.filter((modality) => !modality.linked);

    return {
      rating:
        data.nomeAgenciaRating && data.nomeNotaRating
          ? `${data.nomeAgenciaRating} (${data.nomeNotaRating})`
          : '--',
      riskRating: data.nomeClassificacaoRisco || 'Sem classificação',
      riskRatingCode: data.codigoClassificacaoRisco || 0,
      deadlineMonthsFormatted: data.prazoEmMeses
        ? `${data.prazoEmMeses} ${data.prazoEmMeses > 1 ? 'meses' : 'mês'}`
        : '--',
      issuer: data.emissorSecuritizadora || '--',
      unitaryPrice,
      unitaryPriceFormatted: Utils.Mask.Money(unitaryPrice),
      valueAlreadyAssignedFormatted: data.valorReservaOferta
        ? Utils.Mask.Money(data.valorReservaOferta)
        : '',
      rules: data.regrasCorretora || '',
      linkedModalities: normalizePublicOfferModalitiesWithTimeline(data, linkedModalities),
      unlinkedModalities: normalizePublicOfferModalitiesWithTimeline(data, unlinkedModalities),
    };
  } catch (_error) {
    throw new Error('Não foi possível carregar os detalhes do ativo para investimento.');
  }
};

export const normalizePublicOffersSearch = (
  data: ResponsesPublicOfferingResponse[]
): PublicOfferEntityNormalized[] => {
  if (!data || !data.length) {
    return [];
  }

  try {
    return data.map((publicOffer) => ({
      publicOfferType: publicOffer.offeringType.id,
      offerId: publicOffer.offeringNumber || 0,
      offerType: publicOffer.offeringType?.name || '',
      offerTypeId: publicOffer.offeringType?.id.toString() ?? '0',
      suitabilityProfile: publicOffer.investorSuitabilityProfile || publicOffer.riskClassName || '',
      isQualified: publicOffer.isQualifiedInvestor || false,
      isProfessional: publicOffer.isProfessionalInvestor || false,
      companyName: publicOffer.issuerName || publicOffer.issuerShortName || '',
      offerName: publicOffer.offeringName || publicOffer.tickerDescription || '',
      isinCode: publicOffer.isin || '',
      pricingMethod: publicOffer.pricingMethod || '',
      rateLimiterTranslated: publicOffer.rateLimiterTranslated || '',
      alterationDateNormalized: publicOffer.lastModifiedDate
        ? new Date(publicOffer.lastModifiedDate).getTime()
        : 0,
      alterationDateFormatted: publicOffer.lastModifiedDate
        ? Utils.Mask.DateFormat(new Date(publicOffer.lastModifiedDate), 'dd/MM/yyyy')
        : '--',
      negotiationDateNormalized: publicOffer.tradingStartDate
        ? new Date(publicOffer.tradingStartDate).getTime()
        : 0,
      negotiationDateFormatted: publicOffer.tradingStartDate
        ? Utils.Mask.DateFormat(new Date(publicOffer.tradingStartDate), 'dd/MM/yyyy')
        : '--',
      liquidityDateNormalized: publicOffer.settlementDate
        ? new Date(publicOffer.settlementDate).getTime()
        : 0,
      liquidityDateFormatted: publicOffer.settlementDate
        ? Utils.Mask.DateFormat(new Date(publicOffer.settlementDate), 'dd/MM/yyyy')
        : '--',
      limiter: publicOffer.rateLimiter || '',
      limiterCode: 0,
      deadlineMonths: publicOffer.termInMonths || 0,
      minimumRemuneration: publicOffer.minimumRate || 0,
      maximumRemuneration: publicOffer.maximumRate || 100000000,
      maximumRemunerationAsReceived: publicOffer.maximumRate ?? undefined,
      minimumEquityValue: publicOffer.minimumRate || 0,
      maximumEquityValue: publicOffer.maximumRate || 100000000,
      relationshipIndex: publicOffer.indexationType || '',
      benchmarkName: publicOffer.indexationType || '',
      documents: (publicOffer.offeringDocuments || []).map((file) => ({
        fileAvailable: !file.isDeleted,
        fileName: file.fileName || '',
        fileUrl: file.fileUrl || '',
      })),
    }));
  } catch (_error) {
    return [];
  }
};

export const normalizePublicOfferAvailableProductsDetails = (
  data: PublicOfferingOptionsByOfferIdSuccessListResponse
): PublicOfferDetailsNormalized => {
  if (!data || !data.data || !data.data.length) {
    return { modalities: [] };
  }

  try {
    return {
      modalities: data.data.map((modality) => {
        const isQuantity = modality.modalityType === 'QuantidadeAcoes';
        const unitaryPrice = 1;

        const minimumApplication = modality.minimumAdhesionValue;
        const minimumQuantity = isQuantity
          ? modality.minimumAdhesionValue
          : modality.minimumAdhesionValue / unitaryPrice;

        const maximumApplication = modality.maximumAdhesionValue;
        const maximumQuantity = isQuantity
          ? modality.maximumAdhesionValue
          : modality.maximumAdhesionValue / unitaryPrice;

        return {
          codigoModalidade: modality.code,
          numeroOferta: modality.offerNumber,
          dataFimReserva: modality.reservationEndDate.toString(),
          dataInicioReserva: modality.reservationStartDate.toString(),
          descricaoModalidade: modality.description || '',
          nomeModalidade: modality.name || '',
          tipoModalidade: modality.modalityType || '',
          valorMaxAdesao: modality.maximumAdhesionValue,
          valorMinAdesao: modality.minimumAdhesionValue,
          vinculado: modality.linked,
          modalityName: modality.name || '',
          modalityDescription: modality.description || '',
          modalityType: modality.modalityType || '',
          reservationStartDateFormatted: Utils.Mask.DateFormat(
            new Date(modality.reservationStartDate),
            'dd/MM/yyyy'
          ),
          reservationEndDateFormatted: Utils.Mask.DateFormat(
            new Date(modality.reservationEndDate),
            'dd/MM/yyyy'
          ),
          minimumQuantity,
          maximumQuantity,
          minimumApplication,
          maximumApplication,
          minimumApplicationFormatted: isQuantity
            ? Utils.Mask.Quantity(minimumApplication)
            : Utils.Mask.Money(minimumApplication),
          maximumApplicationFormatted: isQuantity
            ? Utils.Mask.Quantity(maximumApplication)
            : Utils.Mask.Money(maximumApplication),
          isExpired: Utils.Date.IsPast(new Date(modality.reservationEndDate)),
          linked: modality.linked,
        } as PublicOfferDetailsModalityNormalized;
      }),
    };
  } catch (_error) {
    return { modalities: [] };
  }
};
